import React, { useContext, useMemo, useEffect } from "react";

import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClearIcon from "@mui/icons-material/Clear";

import { ApiDataContext } from "../../contexts/ApiDataContext";
import { FilterContext } from "../../contexts/FilterContext";

export default function Filter() {
  const [
    selectedProgress,
    setSelectedProgress,
    selectedStages,
    setSelectedStages,
    selectedPDCs,
    setSelectedPDCs,
    selectedIntegrators,
    setSelectedIntegrators,
    selectedStates,
    setSelectedStates,
  ] = useContext(FilterContext);

  const { apiData } = useContext(ApiDataContext);

  // Memoize options to prevent re-renders
  const pdcOptions = useMemo(() => apiData.powerDistributionCompanies || [], [apiData.powerDistributionCompanies]);
  const clientOptions = useMemo(() => apiData.clients || [], [apiData.clients]);
  const stateOptions = useMemo(() => apiData.states || [], [apiData.states]);

  // Restaurar TODOS os filtros do sessionStorage na montagem
  useEffect(() => {
    // Restaurar Etapas
    const savedStages = sessionStorage.getItem("selectedStages");
    if (savedStages) {
      try {
        const parsed = JSON.parse(savedStages);
        if (parsed && parsed.length > 0) {
          setSelectedStages(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedStages:", e);
      }
    }

    // Restaurar Concessionárias
    const savedPDCs = sessionStorage.getItem("selectedPDCs");
    if (savedPDCs) {
      try {
        const parsed = JSON.parse(savedPDCs);
        if (parsed && parsed.length > 0) {
          setSelectedPDCs(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedPDCs:", e);
      }
    }

    // Restaurar Integradores
    const savedIntegrators = sessionStorage.getItem("selectedIntegrators");
    if (savedIntegrators) {
      try {
        const parsed = JSON.parse(savedIntegrators);
        if (parsed && parsed.length > 0) {
          setSelectedIntegrators(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedIntegrators:", e);
      }
    }

    // Restaurar Estados
    const savedStates = sessionStorage.getItem("selectedStates");
    if (savedStates) {
      try {
        const parsed = JSON.parse(savedStates);
        if (parsed && parsed.length > 0) {
          setSelectedStates(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedStates:", e);
      }
    }
  }, [setSelectedStages, setSelectedPDCs, setSelectedIntegrators, setSelectedStates]);

  const handleClearFilters = () => {
    setSelectedStages([]);
    setSelectedPDCs([]);
    setSelectedIntegrators([]);
    setSelectedStates([]);
    
    // Limpar sessionStorage
    sessionStorage.removeItem("selectedStages");
    sessionStorage.removeItem("selectedPDCs");
    sessionStorage.removeItem("selectedIntegrators");
    sessionStorage.removeItem("selectedStates");
  };

  const hasAnyFilter = 
    selectedStages.length > 0 || 
    selectedPDCs.length > 0 || 
    selectedIntegrators.length > 0 || 
    selectedStates.length > 0;

  return (
    <Box>
      {/* Botão Limpar Filtros */}
      {hasAnyFilter && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{
              borderColor: "#D1D5DB",
              color: "#6B7280",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Limpar Filtros
          </Button>
        </Box>
      )}

      {/* Grid 2x2 para os Filtros */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <StagesDropdown />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            options={pdcOptions}
            getOptionLabel={(option) => option.name}
            value={selectedPDCs}
            onChange={(event, newValue) => {
              setSelectedPDCs(newValue);
              sessionStorage.setItem("selectedPDCs", JSON.stringify(newValue));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Concessionária"
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            options={clientOptions}
            getOptionLabel={(option) => option.name}
            value={selectedIntegrators}
            onChange={(event, newValue) => {
              setSelectedIntegrators(newValue);
              sessionStorage.setItem("selectedIntegrators", JSON.stringify(newValue));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Integrador"
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            options={stateOptions}
            getOptionLabel={(option) => option.name}
            value={selectedStates}
            onChange={(event, newValue) => {
              setSelectedStates(newValue);
              sessionStorage.setItem("selectedStates", JSON.stringify(newValue));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Estado (PDC)"
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                  },
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function StagesDropdown() {
  const [
    selectedProgress,
    setSelectedProgress,
    selectedStages,
    setSelectedStages,
  ] = useContext(FilterContext);

  const { apiData } = useContext(ApiDataContext);

  const isAllSelected =
    apiData.stages.length > 0 &&
    selectedStages.length === apiData.stages.length;

  const handleChange = (event) => {
    const value = event.target.value;

    // Verifica se "all" foi clicado (pode estar em qualquer posição do array)
    if (value.includes("all")) {
      // Toggle: se todas estão selecionadas → desmarca tudo, senão → marca tudo
      if (selectedStages.length === apiData.stages.length) {
        setSelectedStages([]);
        sessionStorage.removeItem("selectedStages");
      } else {
        setSelectedStages(apiData.stages);
        sessionStorage.setItem("selectedStages", JSON.stringify(apiData.stages));
      }
      return;
    }
    
    // Seleção individual normal (remove "all" do array se existir)
    const filteredValue = value.filter(item => item !== "all");
    setSelectedStages(filteredValue);
    sessionStorage.setItem("selectedStages", JSON.stringify(filteredValue));
  };

  const isChecked = (value) => {
    return selectedStages.some((e) => e.id === value.id);
  };

  return (
    <FormControl 
      sx={{ 
        width: "100%",
        backgroundColor: "#FFFFFF",
        borderRadius: 1.5,
      }} 
      size="small"
    >
      <InputLabel id="mutiple-select-label">Etapas</InputLabel>
      <Select
        labelId="mutiple-select-label"
        multiple
        value={selectedStages}
        onChange={handleChange}
        input={<OutlinedInput label="Etapas" />}
        renderValue={(selectedStages) => {
          if (selectedStages.length === 0) return "";
          if (selectedStages.length === 1) return selectedStages[0].stageName;
          return `${selectedStages.length} etapas selecionadas`;
        }}
        MenuProps={MenuProps}
      >
        <MenuItem value="all">
          <ListItemIcon>
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selectedStages.length > 0 &&
                selectedStages.length < apiData.stages.length
              }
            />
          </ListItemIcon>
          <ListItemText>
            <Typography sx={{ fontWeight: "bold" }}>
              Selecione todos
            </Typography>
          </ListItemText>
        </MenuItem>

        {apiData.stages.map((value) => (
          <MenuItem key={value.id} value={value}>
            <Checkbox checked={isChecked(value)} />
            <ListItemText primary={value.stageName} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
